import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Brain, Shield, TrendingUp, Heart, Briefcase, Users, BookOpen, Lock, Eye, Upload, GraduationCap, Sparkles } from 'lucide-react';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import { mockConversations } from '../mock';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Parallax mouse effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Subtle particle animation
    const canvas = document.getElementById('particles-canvas');
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 50;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.5,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3
      });
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(6, 182, 212, 0.1)';

      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="bg-black text-white">
      <PageTitle title="Home - More Than Memory, A Presence" />
      {/* BLOCK 1 - HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <canvas id="particles-canvas" className="absolute inset-0" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-40 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              {/* Ethics Badge */}
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-sm text-cyan-400">
                <Shield size={16} />
                <span>{t('home.ethicsBadge')}</span>
              </div>
              
              <h1 className="text-6xl lg:text-8xl font-bold tracking-tight">
                RepresenX
              </h1>
              <h2 className="text-3xl lg:text-4xl text-cyan-400 font-light">
                {t('home.tagline')}
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                {t('home.description')}
              </p>
              
              <p className="text-sm text-gray-500 max-w-xl">
                {t('home.disclaimer')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/create">
                  <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105">
                    {t('home.createYourPresence')}
                  </Button>
                </Link>
                <Link to="/technology">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 text-lg px-8 py-6 transition-all duration-300">
                    {t('home.learnTechnology')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Logo Animation */}
            <div className="flex items-center justify-center">
              <div 
                className="relative"
                style={{
                  transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_layout-kit-1/artifacts/rhut9t2z_Represenx_logo.png" 
                  alt="RepresenX Logo" 
                  className="w-full max-w-md lg:max-w-lg animate-breathe"
                />
                <div className="absolute inset-0 bg-cyan-500/20 blur-3xl rounded-full animate-glow-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 2 - PRESENCE ENGINE */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-black via-[#0B1F2E] to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 reveal-on-scroll">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">{t('home.presenceEngineTitle')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('home.presenceEngineSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Brain,
                title: t('home.voiceStyleTitle'),
                description: t('home.voiceStyleDesc')
              },
              {
                icon: Heart,
                title: t('home.valueMappingTitle'),
                description: t('home.valueMappingDesc')
              },
              {
                icon: TrendingUp,
                title: t('home.behaviorTitle'),
                description: t('home.behaviorDesc')
              },
              {
                icon: Upload,
                title: t('home.socialMediaImportTitle'),
                description: t('home.socialMediaImportDesc')
              }
            ].map((feature, idx) => (
              <Card 
                key={idx} 
                className={`bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 lift-on-hover neon-border-hover group reveal-on-scroll`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-8 space-y-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-lg flex items-center justify-center group-hover:from-cyan-500/30 group-hover:to-cyan-600/20 transition-all duration-300 tilt-on-hover">
                    <feature.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-2xl font-semibold">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 3 - DEMO PREVIEW */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-16 reveal-on-scroll">{t('home.presenceInActionTitle')}</h2>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Conversation Demo */}
            <div className="space-y-6 reveal-on-scroll">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-cyan-500/20 animate-scale-in smooth-glow">
                <p className="text-gray-300 mb-2 text-sm font-medium">You:</p>
                <p className="text-white text-lg">"{mockConversations[0].question}"</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-900/20 to-cyan-800/10 rounded-2xl p-6 border border-cyan-500/40 shadow-lg shadow-cyan-500/10 animate-scale-in smooth-glow neon-border-hover" style={{ animationDelay: '0.2s' }}>
                <p className="text-cyan-400 mb-2 text-sm font-medium">RepresenX:</p>
                <p className="text-white text-lg">"{mockConversations[0].response}"</p>
              </div>
            </div>

            {/* Animated Silhouette */}
            <div className="flex items-center justify-center reveal-on-scroll">
              <div className="relative w-80 h-80">
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  <defs>
                    <linearGradient id="face-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#0891b2" stopOpacity="0.3" />
                    </linearGradient>
                  </defs>
                  <circle cx="100" cy="100" r="80" fill="none" stroke="url(#face-gradient)" strokeWidth="2" className="animate-glow-pulse" />
                  <path d="M 60 80 Q 100 60 140 80" fill="none" stroke="#06b6d4" strokeWidth="2" opacity="0.7" className="animate-wave" />
                  <path d="M 60 130 Q 100 150 140 130" fill="none" stroke="#06b6d4" strokeWidth="2" opacity="0.7" className="animate-glow-pulse" />
                  <circle cx="70" cy="90" r="3" fill="#06b6d4" className="animate-ping" />
                  <circle cx="130" cy="90" r="3" fill="#06b6d4" className="animate-ping" style={{animationDelay: '0.5s'}} />
                </svg>
                <div className="absolute inset-0 bg-cyan-500/10 blur-2xl rounded-full animate-glow-pulse" />
              </div>
            </div>
          </div>

          <p className="text-center text-gray-300 text-lg mt-12 max-w-2xl mx-auto">
            {t('home.presenceInActionDesc')}
          </p>
        </div>
      </section>

      {/* BLOCK 3.5 - TRAINING & LEARNING */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 reveal-on-scroll">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">{t('home.trainingTitle')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('home.trainingSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: GraduationCap,
                title: t('home.trainingModuleTitle'),
                description: t('home.trainingModuleDesc'),
                features: [
                  t('home.trainingFeature1'),
                  t('home.trainingFeature2'),
                  t('home.trainingFeature3')
                ]
              },
              {
                icon: Sparkles,
                title: t('home.autoLearningTitle'),
                description: t('home.autoLearningDesc'),
                features: [
                  t('home.autoLearningFeature1'),
                  t('home.autoLearningFeature2'),
                  t('home.autoLearningFeature3')
                ]
              }
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-br from-purple-900/30 to-cyan-900/30 border-purple-500/30 hover:border-purple-500/60 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/20 group reveal-on-scroll lift-on-hover"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-10 space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 energy-pulse">
                    <item.icon className="text-purple-400" size={32} />
                  </div>
                  <div className="h-1 w-16 bg-gradient-to-r from-purple-500 to-cyan-500 liquid-flow" />
                  <h3 className="text-2xl font-bold">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.description}</p>
                  <ul className="space-y-3">
                    {item.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-purple-400" />
                        </div>
                        <span className="text-gray-400">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 4 - USE CASES */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-black to-[#0B1F2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-16 reveal-on-scroll">{t('home.useCasesTitle')}</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Heart,
                title: t('home.legacyTitle'),
                description: t('home.legacyDesc')
              },
              {
                icon: Users,
                title: t('home.companionTitle'),
                description: t('home.companionDesc')
              },
              {
                icon: Briefcase,
                title: t('home.professionalTitle'),
                description: t('home.professionalDesc')
              },
              {
                icon: BookOpen,
                title: t('home.memoryTitle'),
                description: t('home.memoryDesc')
              }
            ].map((useCase, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 border-cyan-500/30 hover:border-cyan-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20 group cursor-pointer lift-on-hover neon-border-hover reveal-on-scroll"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-10 space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 energy-pulse">
                    <useCase.icon className="text-cyan-400" size={32} />
                  </div>
                  <h3 className="text-2xl font-semibold">{useCase.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-lg">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 5 - ETHICS */}
      <section className="py-24 lg:py-32 bg-[#111] relative overflow-hidden">
        {/* Floating particles background */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(10)].map((_, i) => (
            <div 
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-8 reveal-on-scroll">{t('home.ethicsTitle')}</h2>
            <p className="text-xl text-gray-300 leading-relaxed reveal-on-scroll max-w-4xl mx-auto">
              {t('home.ethicsSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              {
                icon: Shield,
                title: t('home.consentTitle'),
                description: t('home.consentDesc')
              },
              {
                icon: Lock,
                title: t('home.boundariesTitle'),
                description: t('home.boundariesDesc')
              },
              {
                icon: Eye,
                title: t('home.transparencyTitle'),
                description: t('home.transparencyDesc')
              }
            ].map((item, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 border-cyan-500/30 hover:border-cyan-500/60 transition-all duration-300 reveal-on-scroll" style={{ transitionDelay: `${idx * 100}ms` }}>
                <CardContent className="p-8 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl energy-pulse">
                    <item.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/ethics">
              <Button variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
                {t('home.readEthicsPolicy')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
